import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../services/api';

type Look = {
  id: string;
  name: string;
  description: string | null;
  lookId: string;
  previewUrl: string | null;
  isDefault: boolean;
  isChromakey: boolean;
  status: string;
};

type PlateView = {
  view: string;
  label: string;
  url: string | null;
  status: 'pending' | 'ready' | 'error' | string;
  generatedAt: string | null;
  error: string | null;
};

type Avatar = {
  id: string;
  name: string;
  avatarId: string;
  voiceId: string | null;
  voicePreviewUrl: string | null;
  voiceSampleVideoUrl: string | null;
  voiceProcessingStatus: 'pending' | 'ready' | 'error' | null;
  voiceProcessingError: string | null;
  previewUrl: string | null;
  photoUrl: string | null;
  status: string;
  errorMessage: string | null;
  looks: Look[];
  referencePlateUrl: string | null;
  referencePlateViews: PlateView[] | null;
  referencePlateStatus: 'pending' | 'ready' | 'partial' | 'error' | null;
};

// Custo em créditos por vista gerada (painel completo = 4 vistas).
const PLATE_VIEW_COST = 1;

// ── helpers ──────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; color: string }> = {
    pending:  { label: 'Processando...', color: '#f59e0b' },
    ready:    { label: 'Pronto',       color: '#10b981' },
    error:    { label: 'Erro',         color: '#ef4444' },
  };
  const s = map[status] || { label: status, color: '#6b7280' };
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
      background: s.color + '22', color: s.color, border: `1px solid ${s.color}44`,
    }}>{s.label}</span>
  );
}

function UploadButton({
  label, accept, onFile, loading, disabled,
}: { label: string; accept: string; onFile: (f: File) => void; loading?: boolean; disabled?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <input ref={ref} type="file" accept={accept} style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) { onFile(f); e.target.value = ''; } }} />
      <button
        onClick={() => ref.current?.click()}
        disabled={loading || disabled}
        style={{
          padding: '8px 16px', borderRadius: 8, border: '1px solid #374151',
          background: '#1f2937', color: '#e5e7eb', cursor: 'pointer', fontSize: 13,
          opacity: loading || disabled ? 0.5 : 1,
        }}
      >
        {loading ? 'Enviando...' : label}
      </button>
    </>
  );
}

// ── main component ────────────────────────────────────────────────────────────

export function AvatarGallery() {
  const [avatar, setAvatar] = useState<Avatar | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingVoice, setUploadingVoice] = useState(false);
  const [uploadingLook, setUploadingLook] = useState(false);
  const [avatarName, setAvatarName] = useState('');
  const [editingLook, setEditingLook] = useState<string | null>(null);
  const [editLookName, setEditLookName] = useState('');
  const [editLookDesc, setEditLookDesc] = useState('');
  const [savingLook, setSavingLook] = useState(false);
  const [voiceMode, setVoiceMode] = useState<'id' | 'clone'>('clone');
  const [voiceIdInput, setVoiceIdInput] = useState('');
  const [savingVoiceId, setSavingVoiceId] = useState(false);
  const [voiceNotice, setVoiceNotice] = useState<string | null>(null);
  // Guarda o motor que gerou a amostra de voz exibida nesta tela. Se o usuário
  // trocar de motor, a amostra abaixo passa a ser de outro motor e deixa de
  // representar o que sairá nos próximos vídeos — avisamos em vez de esconder.
  const [plateBusy, setPlateBusy] = useState<string | null>(null);
  const [plateNotice, setPlateNotice] = useState<string | null>(null);
  // Painel de ajuste da regeração de UMA vista (mesmo padrão do ajuste de
  // imagem/cena das produções): qual vista está aberta, texto do ajuste e foto
  // de referência opcional. Ambos os campos são opcionais.
  const [plateAdjustView, setPlateAdjustView] = useState<string | null>(null);
  const [plateFeedback, setPlateFeedback] = useState('');
  const [plateRefUrl, setPlateRefUrl] = useState('');
  const [plateRefUploading, setPlateRefUploading] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Enquanto qualquer processo em segundo plano estiver rodando, a tela faz polling.
  const isProcessing = (a: any) =>
    a?.status === 'pending' || a?.voiceProcessingStatus === 'pending' || a?.referencePlateStatus === 'pending';

  const load = async () => {
    try {
      const res = await api.getMyAvatar();
      setAvatar(res.avatar);
      if (isProcessing(res.avatar)) startPolling();
      else stopPolling();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const startPolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.getMyAvatar();
        setAvatar(res.avatar);
        if (!isProcessing(res.avatar)) {
          stopPolling();
          if (res.avatar?.voiceProcessingStatus === 'ready') setVoiceNotice('Voz pronta para uso.');
        }
      } catch {}
    }, 5000);
  };

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => { load(); return () => stopPolling(); }, []);

  const handleUploadAvatar = async (file: File) => {
    setUploadingAvatar(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('photo', file);
      if (avatarName.trim()) form.append('name', avatarName.trim());
      const res = await api.uploadAvatarPhoto(form);
      setAvatar(res.avatar);
      if (res.avatar?.status === 'pending') startPolling();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleUploadVoice = async (file: File) => {
    if (!avatar) return;
    setUploadingVoice(true);
    setError(null);
    setVoiceNotice(null);
    try {
      const form = new FormData();
      form.append('audio', file);
      const res = await api.uploadAvatarVoice(avatar.id, form);
      // Preserva looks: o retorno do update pode não incluí-los.
      setAvatar(prev => {
        const updated = (res as any).avatar;
        if (updated) return { ...prev, ...updated, looks: updated.looks ?? prev?.looks ?? [] };
        return prev ? { ...prev, voiceId: (res as any).voiceId, voicePreviewUrl: null } : prev;
      });
      setVoiceNotice(res.message || 'Áudio recebido. A clonagem continuará em segundo plano e pode levar alguns minutos. Volte aqui em alguns minutos.');
      startPolling();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setUploadingVoice(false);
    }
  };

  const handleUploadLook = async (file: File) => {
    if (!avatar) return;
    setUploadingLook(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('photo', file);
      const res = await api.uploadAvatarLook(avatar.id, form);
      setAvatar(prev => prev ? { ...prev, looks: [...(prev.looks || []), (res as any).look] } : prev);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setUploadingLook(false);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!avatar) return;
    if (!window.confirm('Remover este avatar? Esta ação não pode ser desfeita.')) return;
    try {
      await api.deleteMarketingAvatar(avatar.id);
      setAvatar(null);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleDeleteLook = async (lookId: string) => {
    if (!avatar) return;
    if (!window.confirm('Remover este look?')) return;
    try {
      await api.deleteMarketingAvatarLook(avatar.id, lookId);
      setAvatar(prev => prev ? { ...prev, looks: (prev.looks || []).filter(l => l.id !== lookId) } : prev);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleSaveVoiceId = async () => {
    if (!avatar || !voiceIdInput.trim()) return;
    setSavingVoiceId(true);
    setError(null);
    setVoiceNotice(null);
    try {
      const res = await api.updateMarketingAvatar(avatar.id, { voiceId: voiceIdInput.trim() });
      if ((res as any).avatar) {
        const updated = (res as any).avatar;
        // Preserva looks: o retorno do update pode não incluí-los.
        setAvatar(prev => ({ ...prev, ...updated, looks: updated.looks ?? prev?.looks ?? [] }));
      } else {
        setAvatar(prev => prev ? { ...prev, voiceId: voiceIdInput.trim(), voicePreviewUrl: null } : prev);
      }
      setVoiceIdInput('');
      setVoiceNotice(res.message || 'Voice ID salvo. A preparação continuará em segundo plano. Volte aqui em alguns minutos.');
      if ((res as any).processing) startPolling();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSavingVoiceId(false);
    }
  };


  // ── Painel de referência ────────────────────────────────────────────────────

  const applyPlateResponse = (res: any) => {
    setAvatar(prev => prev ? {
      ...prev,
      referencePlateUrl: res.referencePlateUrl ?? null,
      referencePlateViews: res.referencePlateViews ?? null,
      referencePlateStatus: res.referencePlateStatus ?? null,
    } : prev);
    setPlateNotice(res.message || null);
    startPolling();
  };

  const handleGeneratePlate = async () => {
    if (!avatar) return;
    const jaExiste = !!avatar.referencePlateStatus;
    if (jaExiste && !window.confirm('Refazer as 4 vistas do painel? Serão cobrados 4 créditos.')) return;
    setPlateBusy('all');
    setError(null);
    setPlateNotice(null);
    try {
      applyPlateResponse(await api.generateAvatarReferencePlate(avatar.id));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setPlateBusy(null);
    }
  };

  const openPlateAdjust = (view: PlateView) => {
    setPlateAdjustView(view.view);
    setPlateFeedback('');
    setPlateRefUrl('');
    setPlateNotice(null);
  };

  const closePlateAdjust = () => {
    setPlateAdjustView(null);
    setPlateFeedback('');
    setPlateRefUrl('');
  };

  // A foto de ajuste usa o MESMO upload de referência da regeração de imagem/cena:
  // sobe para o MinIO e o que trafega depois é só a URL.
  const uploadPlateRef = async (file: File) => {
    setPlateRefUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const res = await api.uploadEcommerceReference(fd);
      setPlateRefUrl(res.imageUrl);
    } catch (e: any) {
      setError(e?.message || 'Erro ao enviar a foto de referência.');
    } finally {
      setPlateRefUploading(false);
    }
  };

  const handleRegeneratePlateView = async (view: PlateView) => {
    if (!avatar) return;
    if (!window.confirm(`Refazer a vista "${view.label}"? Será cobrado ${PLATE_VIEW_COST} crédito.`)) return;
    setPlateBusy(view.view);
    setError(null);
    setPlateNotice(null);
    try {
      applyPlateResponse(await api.regenerateAvatarReferencePlateView(avatar.id, view.view, {
        feedback: plateFeedback.trim() || null,
        referenceImageUrl: plateRefUrl || null,
      }));
      closePlateAdjust();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setPlateBusy(null);
    }
  };

  const startEditLook = (look: Look) => {
    setEditingLook(look.id);
    setEditLookName(look.name);
    setEditLookDesc(look.description || '');
  };

  const cancelEditLook = () => {
    setEditingLook(null);
    setEditLookName('');
    setEditLookDesc('');
  };

  const saveLook = async (lookId: string) => {
    if (!avatar) return;
    setSavingLook(true);
    try {
      const res = await api.updateMarketingAvatarLook(avatar.id, lookId, { name: editLookName.trim() || 'Look', description: editLookDesc.trim() || null });
      setAvatar(prev => prev ? { ...prev, looks: (prev.looks || []).map(l => l.id === lookId ? { ...l, ...res.look } : l) } : prev);
      cancelEditLook();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSavingLook(false);
    }
  };

  // ── render ──────────────────────────────────────────────────────────────────

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, color: '#6b7280' }}>
      Carregando...
    </div>
  );

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px' }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#f9fafb', marginBottom: 24 }}>Avatar</h2>

      {error && (
        <div style={{
          background: '#7f1d1d', color: '#fca5a5', padding: '10px 14px', borderRadius: 8,
          fontSize: 13, marginBottom: 16,
        }}>
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: 12, background: 'none', border: 'none', color: '#fca5a5', cursor: 'pointer' }}>×</button>
        </div>
      )}

      {!avatar ? (
        // ── estado vazio: formulário de upload ──────────────────────────────
        <div style={{
          border: '2px dashed #374151', borderRadius: 12, padding: 40,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
        }}>
          <div style={{ fontSize: 48 }}>AI</div>
          <p style={{ color: '#9ca3af', textAlign: 'center', margin: 0 }}>
            Nenhum avatar cadastrado ainda.<br />Envie uma foto para criar o seu.
          </p>
          <input
            type="text"
            placeholder="Nome do avatar (opcional)"
            value={avatarName}
            onChange={e => setAvatarName(e.target.value)}
            style={{
              background: '#1f2937', border: '1px solid #374151', borderRadius: 8,
              color: '#e5e7eb', padding: '8px 12px', fontSize: 14, width: 260,
            }}
          />
          <UploadButton
            label="Selecionar foto"
            accept="image/jpeg,image/png,image/webp"
            onFile={handleUploadAvatar}
            loading={uploadingAvatar}
          />
          <p style={{ color: '#6b7280', fontSize: 12, margin: 0 }}>
            JPG, PNG ou WebP · máximo 50 MB
          </p>
        </div>
      ) : (
        // ── avatar existente ─────────────────────────────────────────────────
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

          {/* Card do avatar */}
          <div style={{
            background: '#111827', border: '1px solid #1f2937', borderRadius: 12,
            padding: 20, display: 'flex', gap: 20, alignItems: 'flex-start',
          }}>
            {avatar.previewUrl ? (
              <img src={avatar.previewUrl} alt={avatar.name}
                style={{ width: 96, height: 96, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }} />
            ) : (
              <div style={{
                width: 96, height: 96, borderRadius: 12, background: '#1f2937',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36, flexShrink: 0,
              }}>AI</div>
            )}

            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <span style={{ fontWeight: 700, color: '#f9fafb', fontSize: 16 }}>{avatar.name}</span>
                <StatusBadge status={avatar.status} />
              </div>

              {avatar.status === 'pending' && (
                <p style={{ color: '#9ca3af', fontSize: 13, margin: '0 0 8px' }}>
                  Criando avatar... isso pode levar alguns minutos.
                </p>
              )}
              {avatar.status === 'error' && avatar.errorMessage && (
                <p style={{ color: '#f87171', fontSize: 13, margin: '0 0 8px' }}>{avatar.errorMessage}</p>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                {avatar.status === 'ready' && (
                  <span style={{ fontSize: 12, color: '#6b7280' }}>
                    {avatar.voiceId ? 'Voz Voz clonada' : 'Voz Sem voz clonada'}
                  </span>
                )}
                <button onClick={handleDeleteAvatar}
                  style={{
                    marginLeft: 'auto', padding: '4px 12px', borderRadius: 6, fontSize: 12,
                    background: 'transparent', border: '1px solid #374151', color: '#9ca3af', cursor: 'pointer',
                  }}>
                  Remover avatar
                </button>
              </div>
            </div>
          </div>

          {/* Seção do painel de referência */}
          {avatar.photoUrl && (() => {
            const plateStatus = avatar.referencePlateStatus;
            const plateGenerating = plateStatus === 'pending';
            const views = avatar.referencePlateViews || [];
            const busy = plateGenerating || plateBusy !== null;
            return (
              <section>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 600, color: '#d1d5db', margin: 0 }}>Painel de referência</h3>
                  {plateStatus && (
                    <button onClick={handleGeneratePlate} disabled={busy}
                      style={{
                        padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600,
                        background: 'transparent', color: '#9ca3af', border: '1px solid #374151',
                        cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1,
                      }}>
                      {plateBusy === 'all' ? 'Enviando...' : 'Refazer tudo (4 créditos)'}
                    </button>
                  )}
                </div>

                <div style={{
                  background: '#111827', border: '1px solid #1f2937', borderRadius: 10, padding: 16,
                  display: 'flex', flexDirection: 'column', gap: 14,
                }}>
                  <p style={{ color: '#9ca3af', fontSize: 12, margin: 0, lineHeight: 1.5 }}>
                    Quadro com 4 fotos do avatar em ângulos diferentes (frontal, três-quartos, perfil e corpo inteiro),
                    usado como referência de identidade nas gerações de imagem. Sem painel, tudo continua funcionando com a foto original.
                  </p>

                  {plateGenerating && (
                    <div style={{ padding: '10px 12px', borderRadius: 8, background: '#78350f33', border: '1px solid #f59e0b55' }}>
                      <p style={{ color: '#fbbf24', fontSize: 13, fontWeight: 600, margin: 0 }}>Gerando o painel de referência</p>
                      <p style={{ color: '#d1d5db', fontSize: 12, margin: '4px 0 0' }}>
                        São 4 imagens geradas em sequência e isso leva alguns minutos. Você pode sair desta tela e voltar mais tarde.
                      </p>
                    </div>
                  )}
                  {plateStatus === 'partial' && (
                    <div style={{ padding: '10px 12px', borderRadius: 8, background: '#78350f33', border: '1px solid #f59e0b55' }}>
                      <p style={{ color: '#fbbf24', fontSize: 12, margin: 0 }}>
                        Algumas vistas não foram geradas. Use "Refazer" na vista com erro — só a vista refeita é cobrada.
                      </p>
                    </div>
                  )}
                  {plateStatus === 'error' && (
                    <div style={{ padding: '10px 12px', borderRadius: 8, background: '#7f1d1d33', border: '1px solid #ef444455' }}>
                      <p style={{ color: '#f87171', fontSize: 12, margin: 0 }}>Não foi possível gerar o painel. Tente novamente.</p>
                    </div>
                  )}
                  {plateNotice && (
                    <p style={{ color: '#fbbf24', fontSize: 12, margin: 0 }}>{plateNotice}</p>
                  )}

                  {!plateStatus ? (
                    // ── estado vazio ────────────────────────────────────────
                    <div style={{
                      border: '2px dashed #374151', borderRadius: 10, padding: 24,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                    }}>
                      <p style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', margin: 0 }}>
                        Nenhum painel de referência gerado ainda.
                      </p>
                      <button onClick={handleGeneratePlate} disabled={busy}
                        style={{
                          padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                          background: '#6366f1', color: '#fff', border: 'none',
                          cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1,
                        }}>
                        {plateBusy === 'all' ? 'Enviando...' : 'Gerar painel de referência'}
                      </button>
                      <p style={{ color: '#6b7280', fontSize: 12, margin: 0 }}>
                        4 vistas · 4 créditos · alguns minutos
                      </p>
                    </div>
                  ) : (
                    <>
                      {avatar.referencePlateUrl && (
                        <a href={avatar.referencePlateUrl} target="_blank" rel="noreferrer">
                          <img src={avatar.referencePlateUrl} alt="Painel de referência"
                            style={{ width: '100%', borderRadius: 10, display: 'block', background: '#0f172a' }} />
                        </a>
                      )}

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                        {views.map(v => (
                          <div key={v.view} style={{
                            background: '#0f172a', border: '1px solid #1f2937', borderRadius: 8,
                            overflow: 'hidden', display: 'flex', flexDirection: 'column',
                          }}>
                            {v.url ? (
                              <img src={v.url} alt={v.label}
                                style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', display: 'block' }} />
                            ) : (
                              <div style={{
                                width: '100%', aspectRatio: '1 / 1', background: '#1f2937',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#6b7280', fontSize: 11, textAlign: 'center', padding: 6,
                              }}>
                                {v.status === 'pending' ? 'Gerando...' : 'Sem imagem'}
                              </div>
                            )}
                            <div style={{ padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <span style={{ color: '#e5e7eb', fontSize: 11, fontWeight: 600 }}>{v.label}</span>
                              {v.status === 'error' && (
                                <span style={{ color: '#f87171', fontSize: 10, lineHeight: 1.3 }}>{v.error || 'Falhou'}</span>
                              )}
                              <button onClick={() => (plateAdjustView === v.view ? closePlateAdjust() : openPlateAdjust(v))} disabled={busy}
                                style={{
                                  padding: '3px 8px', borderRadius: 6, fontSize: 10,
                                  background: 'transparent',
                                  color: plateAdjustView === v.view ? '#a5b4fc' : '#9ca3af',
                                  border: `1px solid ${plateAdjustView === v.view ? '#6366f1' : '#374151'}`,
                                  cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1,
                                }}>
                                {plateBusy === v.view ? 'Enviando...' : 'Refazer (1 crédito)'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Painel de ajuste da vista: texto do que saiu errado + foto de
                          referência opcional. Refazer sem preencher nada continua
                          funcionando como antes. */}
                      {plateAdjustView && (() => {
                        const target = views.find(v => v.view === plateAdjustView);
                        if (!target) return null;
                        return (
                          <div style={{
                            border: '1px solid #312e81', borderRadius: 10, padding: 12,
                            background: '#0f172a', display: 'flex', flexDirection: 'column', gap: 8,
                          }}>
                            <span style={{ color: '#a5b4fc', fontSize: 12, fontWeight: 700 }}>
                              Refazer a vista "{target.label}"
                            </span>

                            <label style={{ color: '#9ca3af', fontSize: 11 }}>O que ficou errado? (opcional)</label>
                            <textarea
                              value={plateFeedback}
                              onChange={e => setPlateFeedback(e.target.value)}
                              rows={3}
                              maxLength={1000}
                              placeholder="Ex.: o nariz ficou errado; a orelha está deformada; está mais magra que a pessoa real..."
                              style={{
                                width: '100%', resize: 'none', background: '#111827', border: '1px solid #374151',
                                borderRadius: 8, color: '#e5e7eb', padding: '8px 10px', fontSize: 12,
                                fontFamily: 'inherit', boxSizing: 'border-box',
                              }}
                            />
                            <p style={{ color: '#6b7280', fontSize: 11, margin: 0, lineHeight: 1.5 }}>
                              A vista é gerada de novo com a foto original do avatar e o seu ajuste, mantendo figurino,
                              cabelo, luz e fundo iguais. Sem ajuste, é só uma nova tentativa da mesma vista.
                            </p>

                            <label style={{ color: '#9ca3af', fontSize: 11 }}>Foto de referência (opcional)</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              {plateRefUrl ? (
                                <div style={{ position: 'relative' }}>
                                  <img src={plateRefUrl} alt="Foto de referência"
                                    style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover', border: '1px solid #374151', display: 'block' }} />
                                  <button onClick={() => setPlateRefUrl('')}
                                    style={{
                                      position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: 99,
                                      background: '#f43f5e', color: '#fff', border: 'none', fontSize: 10, cursor: 'pointer',
                                      lineHeight: '18px', padding: 0,
                                    }}>×</button>
                                </div>
                              ) : (
                                <UploadButton
                                  label="+ foto"
                                  accept="image/jpeg,image/png,image/webp"
                                  onFile={uploadPlateRef}
                                  loading={plateRefUploading}
                                  disabled={busy}
                                />
                              )}
                              <span style={{ color: '#6b7280', fontSize: 11 }}>
                                Uma foto REAL da mesma pessoa que mostre bem o traço a corrigir (ex.: foto de perfil).
                              </span>
                            </div>

                            <div style={{ padding: '8px 10px', borderRadius: 8, background: '#78350f33', border: '1px solid #f59e0b55' }}>
                              <p style={{ color: '#fbbf24', fontSize: 11, margin: 0 }}>
                                Será cobrado {PLATE_VIEW_COST} crédito por esta vista. Se a vista não sair, o crédito é devolvido.
                              </p>
                            </div>

                            <div style={{ display: 'flex', gap: 8 }}>
                              <button onClick={closePlateAdjust} disabled={plateBusy !== null}
                                style={{
                                  flex: 1, padding: '7px 12px', borderRadius: 8, fontSize: 12,
                                  background: 'transparent', color: '#9ca3af', border: '1px solid #374151',
                                  cursor: plateBusy !== null ? 'default' : 'pointer',
                                }}>
                                Cancelar
                              </button>
                              <button onClick={() => handleRegeneratePlateView(target)} disabled={busy || plateRefUploading}
                                style={{
                                  flex: 1, padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                                  background: '#6366f1', color: '#fff', border: 'none',
                                  cursor: busy || plateRefUploading ? 'default' : 'pointer',
                                  opacity: busy || plateRefUploading ? 0.5 : 1,
                                }}>
                                {plateBusy === target.view ? 'Enviando...' : `Refazer (${PLATE_VIEW_COST} crédito)`}
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              </section>
            );
          })()}

          {/* Seção de voz */}
          {avatar.status === 'ready' && (
            <section>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: '#d1d5db', marginBottom: 12 }}>Voz</h3>
              <div style={{
                background: '#111827', border: '1px solid #1f2937', borderRadius: 10, padding: 16,
                display: 'flex', flexDirection: 'column', gap: 14,
              }}>
                {/* Voz atual */}
                {avatar.voiceId && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 20 }}>🎙️</span>
                    <div style={{ flex: 1 }}>
                      <p style={{ color: '#e5e7eb', fontSize: 13, margin: 0, fontWeight: 600 }}>Voz configurada</p>
                      <p style={{ color: '#6b7280', fontSize: 11, margin: '2px 0 0', fontFamily: 'monospace' }}>ID: {avatar.voiceId}</p>
                      {avatar.voiceSampleVideoUrl && (
                        <video controls src={avatar.voiceSampleVideoUrl}
                          style={{ marginTop: 8, width: '100%', maxHeight: 360, borderRadius: 10, background: '#020617' }} />
                      )}
                    </div>
                  </div>
                )}

                {/* Motor de voz fixo do sistema (decisão de 2026-09-30). */}
                <div style={{ borderTop: avatar.voiceId ? '1px solid #1f2937' : undefined, paddingTop: avatar.voiceId ? 14 : 0 }}>
                  <p style={{ color: '#e5e7eb', fontSize: 13, fontWeight: 600, margin: 0 }}>Motor de voz: ElevenLabs v4</p>
                  <p style={{ color: '#9ca3af', fontSize: 12, margin: '3px 0 0' }}>
                    Todas as falas do avatar e as narrações usam ElevenLabs v4.
                  </p>
                </div>

                {avatar.voiceProcessingStatus === 'pending' && (
                  <div style={{ padding: '10px 12px', borderRadius: 8, background: '#78350f33', border: '1px solid #f59e0b55' }}>
                    <p style={{ color: '#fbbf24', fontSize: 13, fontWeight: 600, margin: 0 }}>Processando a voz no backend</p>
                    <p style={{ color: '#d1d5db', fontSize: 12, margin: '4px 0 0' }}>
                      Isso pode levar alguns minutos. Você pode sair desta tela e voltar mais tarde.
                    </p>
                  </div>
                )}
                {avatar.voiceProcessingStatus === 'error' && (
                  <div style={{ padding: '10px 12px', borderRadius: 8, background: '#7f1d1d33', border: '1px solid #ef444455' }}>
                    <p style={{ color: '#f87171', fontSize: 13, fontWeight: 600, margin: 0 }}>Não foi possível preparar a voz.</p>
                    {avatar.voiceProcessingError && (
                      <p style={{ color: '#d1d5db', fontSize: 12, margin: '4px 0 0' }}>{avatar.voiceProcessingError}</p>
                    )}
                  </div>
                )}
                {voiceNotice && avatar.voiceProcessingStatus !== 'error' && (
                  <p style={{ color: avatar.voiceProcessingStatus === 'ready' ? '#34d399' : '#fbbf24', fontSize: 12, margin: 0 }}>
                    {voiceNotice}
                  </p>
                )}

                {/* Toggle entre modos */}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => setVoiceMode('clone')}
                    style={{
                      padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      background: voiceMode === 'clone' ? '#6366f1' : 'transparent',
                      color: voiceMode === 'clone' ? '#fff' : '#9ca3af',
                      border: voiceMode === 'clone' ? '1px solid #6366f1' : '1px solid #374151',
                    }}>
                    🎤 Enviar áudio para clonar
                  </button>
                  <button
                    onClick={() => setVoiceMode('id')}
                    style={{
                      padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      background: voiceMode === 'id' ? '#6366f1' : 'transparent',
                      color: voiceMode === 'id' ? '#fff' : '#9ca3af',
                      border: voiceMode === 'id' ? '1px solid #6366f1' : '1px solid #374151',
                    }}>
                    🔑 Inserir Voice ID
                  </button>
                </div>

                {/* Modo: clonar via áudio */}
                {voiceMode === 'clone' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ color: '#9ca3af', fontSize: 12, margin: '0 0 8px' }}>
                        Envie um áudio MP3 ou WAV com pelo menos 30 segundos de fala clara. A voz será clonada via HeyGen.
                      </p>
                      {uploadingVoice && (
                        <p style={{ color: '#f59e0b', fontSize: 12, margin: 0 }}>
                          Enviando o áudio para o backend...
                        </p>
                      )}
                    </div>
                    <UploadButton
                      label={avatar.voiceId ? 'Substituir áudio' : 'Enviar áudio'}
                      accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav"
                      onFile={handleUploadVoice}
                      loading={uploadingVoice}
                    />
                  </div>
                )}

                {/* Modo: inserir ID diretamente */}
                {voiceMode === 'id' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <p style={{ color: '#9ca3af', fontSize: 12, margin: 0 }}>
                      Cole o Voice ID do HeyGen (ex: de uma voz já criada na plataforma ou da biblioteca deles).
                    </p>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        type="text"
                        placeholder="ex: 1bd001d9ef2f4a7f97ddefcd26..."
                        value={voiceIdInput}
                        onChange={e => setVoiceIdInput(e.target.value)}
                        style={{
                          flex: 1, background: '#0f172a', border: '1px solid #334155', borderRadius: 7,
                          color: '#e5e7eb', padding: '7px 10px', fontSize: 13, fontFamily: 'monospace',
                        }}
                      />
                      <button
                        onClick={handleSaveVoiceId}
                        disabled={savingVoiceId || !voiceIdInput.trim()}
                        style={{
                          padding: '7px 16px', borderRadius: 7, fontSize: 13, fontWeight: 600,
                          background: '#6366f1', color: '#fff', border: 'none', cursor: 'pointer',
                          opacity: savingVoiceId || !voiceIdInput.trim() ? 0.5 : 1,
                        }}>
                        {savingVoiceId ? 'Salvando...' : 'Salvar ID'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Seção de looks */}
          {avatar.status === 'ready' && (
            <section>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: '#d1d5db', margin: 0 }}>
                  Looks ({(avatar.looks || []).length})
                </h3>
                <UploadButton
                  label="+ Adicionar look"
                  accept="image/jpeg,image/png,image/webp"
                  onFile={handleUploadLook}
                  loading={uploadingLook}
                />
              </div>

              {(avatar.looks || []).length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: 13 }}>
                  Nenhum look ainda. Adicione variações de aparência enviando mais fotos.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {(avatar.looks || []).map(look => {
                    const isEditing = editingLook === look.id;
                    return (
                      <div key={look.id} style={{
                        background: '#111827', border: `1px solid ${isEditing ? '#6366f1' : '#1f2937'}`, borderRadius: 10,
                        overflow: 'hidden', display: 'flex', gap: 0,
                      }}>
                        {/* Miniatura */}
                        <div style={{ width: 80, flexShrink: 0 }}>
                          {look.previewUrl ? (
                            <img src={look.previewUrl} alt={look.name}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', minHeight: 80 }} />
                          ) : (
                            <div style={{
                              width: '100%', minHeight: 80, background: '#1f2937',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24,
                            }}>📷</div>
                          )}
                        </div>

                        {/* Conteúdo */}
                        <div style={{ flex: 1, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {isEditing ? (
                            <>
                              <input
                                value={editLookName}
                                onChange={e => setEditLookName(e.target.value)}
                                placeholder="Nome do look"
                                style={{
                                  background: '#0f172a', border: '1px solid #334155', borderRadius: 6,
                                  color: '#e5e7eb', padding: '4px 8px', fontSize: 13, fontWeight: 600, width: '100%',
                                }}
                              />
                              <textarea
                                value={editLookDesc}
                                onChange={e => setEditLookDesc(e.target.value)}
                                placeholder="Descreva o cenário: roupa, ambiente, iluminação, tom — o que o LLM precisa saber para escolher este look"
                                rows={3}
                                style={{
                                  background: '#0f172a', border: '1px solid #334155', borderRadius: 6,
                                  color: '#e5e7eb', padding: '4px 8px', fontSize: 12, resize: 'vertical', width: '100%',
                                }}
                              />
                              <div style={{ display: 'flex', gap: 8 }}>
                                <button onClick={() => saveLook(look.id)} disabled={savingLook}
                                  style={{
                                    padding: '4px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                                    background: '#6366f1', color: '#fff', border: 'none', cursor: 'pointer',
                                    opacity: savingLook ? 0.5 : 1,
                                  }}>
                                  {savingLook ? 'Salvando...' : 'Salvar'}
                                </button>
                                <button onClick={cancelEditLook}
                                  style={{
                                    padding: '4px 14px', borderRadius: 6, fontSize: 12,
                                    background: 'transparent', color: '#9ca3af', border: '1px solid #374151', cursor: 'pointer',
                                  }}>
                                  Cancelar
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontWeight: 700, color: '#e5e7eb', fontSize: 13 }}>{look.name}</span>
                                <StatusBadge status={look.status} />
                                {look.isDefault && (
                                  <span style={{ fontSize: 10, color: '#6366f1' }}>padrão</span>
                                )}
                                {look.isChromakey && (
                                  <span style={{ fontSize: 10, color: '#22c55e', background: '#14532d', padding: '1px 6px', borderRadius: 4 }}>🟢 chromakey</span>
                                )}
                              </div>
                              {look.description ? (
                                <p style={{ color: '#9ca3af', fontSize: 12, margin: 0, lineHeight: 1.4 }}>{look.description}</p>
                              ) : (
                                <p style={{ color: '#4b5563', fontSize: 12, margin: 0, fontStyle: 'italic' }}>Sem descrição — adicione para o LLM poder escolher este look</p>
                              )}
                              <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                                <button onClick={() => startEditLook(look)}
                                  style={{
                                    padding: '3px 10px', borderRadius: 6, fontSize: 11,
                                    background: 'transparent', color: '#6b7280', border: '1px solid #374151', cursor: 'pointer',
                                  }}>
                                  Editar
                                </button>
                                {!look.isChromakey && (
                                  <button
                                    onClick={async () => {
                                      if (!avatar) return;
                                      await api.updateMarketingAvatarLook(avatar.id, look.id, { isChromakey: true });
                                      setAvatar(prev => prev ? { ...prev, looks: prev.looks.map(l => ({ ...l, isChromakey: l.id === look.id })) } : prev);
                                    }}
                                    style={{
                                      padding: '3px 10px', borderRadius: 6, fontSize: 11,
                                      background: 'transparent', color: '#22c55e', border: '1px solid #166534', cursor: 'pointer',
                                    }}>
                                    Marcar chromakey
                                  </button>
                                )}
                                <button onClick={() => handleDeleteLook(look.id)}
                                  style={{
                                    padding: '3px 10px', borderRadius: 6, fontSize: 11,
                                    background: 'transparent', color: '#ef4444', border: '1px solid #7f1d1d', cursor: 'pointer',
                                  }}>
                                  Remover
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}
        </div>
      )}
    </div>
  );
}
